/**
 * feature-config flag names owned by identity, security, and compliance
 *
 * please consider removing this file and adding it to an appropriate place in the
 * feature code (e.g. an entrypoint, or a package owned by the feature team), or
 * passing flag names directly as a string!
 *
 * by separating it out, we decouple the feature code from the feature config packages
 * and avoid the need to update this package when flags are added or removed.
 */

export const IDSC_FLAG_NAMES = {
  // Admin Tools
  ADMIN_SITE_ACCESS_TOOL: 'ff-idsc-1439-admin-site-access-tool',
  // Granular Access
  CMS_FGAC_DISCOVERABILITY: 'ff-idsc-789-cms-fga-discoverability',
  // GOV-3344: on, the CMS collection fine-grained-access UI is disabled for
  // custom roles without `siteCollectionItem.manage`. UI-only gating — existing
  // restrictions are not cleared. Default OFF.
  CMS_FGAC_DISABLE_WITHOUT_ITEM_PERMS:
    'ff-gov-3344-cms-fga-disable-without-item-perms',
  PRIMARY_CASCADE_PRIMARY_LOCALE_ACCESS: 'ff-idsc-collection-primary',
  USE_CANONICAL_GRACE_MIDDLEWARE: 'ff-idsc-607-use-canonical-grace-middleware',
  WAL_FGA_ENABLED: 'ff-idsc-852-workspace-audit-log-granular-access',

  // AI Governance
  AI_TOGGLE_EXCLUDED: 'ff-idsc-ai-toggle-exclusion-list',
  SITE_AI_PERMISSION_ENFORCEMENT: 'ff-idsc-1293-site-ai-permission-enforcement',
  AI_ATTRIBUTION_SAL: 'ff-idsc-1633-ai-attribution-sal',

  // Performance
  PERFORMANCE_IMPROVEMENTS: 'ff-idsc-728-performance-improvements',
  USER_SIGNUPS: 'ff-user-signups',
  // EU data residency — enterprise-invite-only signup gate (IDSC-1969).
  // Kill switch: the in-code isEuStack() guard is the region gate; this
  // flag arms/disarms the gate without a deploy. Default OFF.
  ENTERPRISE_ONLY_SIGNUPS: 'ff-enterprise-only-signups',
  // SCIM
  SCIM_PROVISIONING_KILL_SWITCH: 'ff-idsc-624-scim-provisioning-kill-switch',

  // Security
  AI_BRIDGE_JWT_ACCEPTANCE: 'ff-idsc-1704-ai-bridge-jwt-acceptance',
  SENSITIVE_PERMISSION_FILTERING: 'ff-idsc-202-sensitive-permission-filtering',
  SENSITIVE_MISSING_CONTEXT_FAIL_CLOSED:
    'ff-gov-1906-sensitive-missing-context-fail-closed',
  // Temporary rollback gate for the role-based permission evaluation service.
  // Off keeps the legacy isAllowed/getPermissions implementations.
  PERMISSION_EVALUATION_SERVICE: 'ff-gov-3047-permission-evaluation-service',
  // Batched permission resolution in getPermissionsForSiteMembers (GOV-3247).
  // On loads shared role maps and capabilities once per site; off keeps the
  // per-member getPermissionsByUserAndSite path. Default OFF.
  BATCH_SITE_MEMBER_PERMISSIONS: 'ff-gov-3247-batch-site-member-permissions',
  // 2FA QR provisioning — on renders the QR code locally; off falls back to
  // quickchart.io (GOV-2684). Staged rollout with a fast kill switch; remove
  // the quickchart.io branch and this flag once fully rolled out.
  MFA_LOCAL_QR_GENERATION: 'ff-idsc-2684-mfa-local-qr-generation',
  // Kill switch for the add-custom-font cross-site file check (GOV-2689).
  // On enforces req.file.site === req.site._id; off reverts to the
  // pre-fix (vulnerable) behavior. Default ON — this is an emergency
  // revert lever, not a staged rollout.
  SCOPE_CUSTOM_FONT_FILE_TO_SITE: 'ff-gov-2689-scope-custom-font-file-site',

  // Custom-role UX checkbox for "Merge approved branches" (GOV-3154). Off keeps
  // the new checkbox hidden and role behavior unchanged; existing roles that
  // hold siteBranch.mergeApproved via standard-role defaults are unaffected.
  // Default OFF.
  MERGE_APPROVED_BRANCH: 'ff-gov-3154-merge-approved-branch',

  // Workspace Audit Log
  AUDIT_LOG: 'ff-dash-4645-audit-log',

  // Google Auth
  FORCE_FEDCM: 'ff-growth-4987-force-fedcm',

  // Client seats — custom roles in client invite & site-member dropdown
  CUSTOM_ROLES_FOR_CLIENT_SEATS: 'ff-idsc-1725-custom-roles-for-client-seats',

  // Credential Intelligence result ingestion. Each approved password-entry
  // flow can be observed and rolled back independently. Default OFF.
  CREDENTIAL_INTELLIGENCE_INGEST_LOGIN:
    'ff-gov-3064-credential-intelligence-login-ingestion',
  CREDENTIAL_INTELLIGENCE_INGEST_SIGNUP:
    'ff-gov-3064-credential-intelligence-signup-ingestion',
  CREDENTIAL_INTELLIGENCE_INGEST_PASSWORD_RESET:
    'ff-gov-3064-credential-intelligence-password-reset-ingestion',

  // SSO return-URL parsing on /sso/redirectURI. On parses everything after the
  // first underscore; off keeps the legacy split, which truncates any return URL
  // containing an underscore of its own. Temporary, remove with the gate.
  SSO_STATE_FIRST_UNDERSCORE: 'ff-sour-1941-sso-state-first-underscore',

  // Custom-role UX split of "Edit site and CMS items without approval" into
  // separate "Edit main site without approval" and "Edit CMS items"
  // checkboxes. On hides the combined checkbox; gates both the dashboard
  // checkboxes and the server-side permission mapping. Default OFF.
  SPLIT_APPROVAL_CHECKBOXES: 'ff-gov-3350-separate-site-and-cms-edits',

  // Custom-role UX grouping of "Manage site settings" and "Use Webflow AI" in
  // one Admin section. Layout only: no permission, rule or server change.
  // Independent of SPLIT_APPROVAL_CHECKBOXES. Default OFF. Remove once fully
  // rolled out.
  GROUP_ADMIN_CHECKBOXES: 'ff-gov-3369-group-admin-checkboxes',

  // Review requester cannot also be a reviewer (GOV-3182). On rejects creating
  // a review that lists the requester as a required or optional reviewer with
  // a 409; off allows it and only logs the attempt. Default OFF.
  // Remove once fully rolled out.
  REVIEW_REQUESTER_CANNOT_BE_REVIEWER:
    'ff-gov-3182-review-requester-cannot-be-reviewer',
};
